import _RetellingFeedbackRetryMutation from "../__generated__/RetellingFeedbackRetryMutation.graphql";
import _RetellingFeedbackQuery from "../__generated__/RetellingFeedbackQuery.graphql";
import { useEffect, useState } from 'react';
import { fetchQuery, graphql, useLazyLoadQuery, useMutation, useRelayEnvironment } from 'react-relay';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { En } from '../i18n/En';
import { t, type Message } from '../i18n/t';
import { POLL_MS, retryCopy, underline, waiting, type Status } from '../listen/feedback.ts';
import { codeFromError } from './aidErrors.ts';
import { HearIt } from './HearIt';
import { usePractise } from './usePractise';
import type { RetellingFeedbackQuery as RetellingFeedbackQueryType } from '../__generated__/RetellingFeedbackQuery.graphql';
import type { RetellingFeedbackRetryMutation } from '../__generated__/RetellingFeedbackRetryMutation.graphql';
const RetellingFeedbackQuery = (_RetellingFeedbackQuery.hash && _RetellingFeedbackQuery.hash !== "f1f43187a4c9fce4b06c125702cb3718" && console.error("The definition of 'RetellingFeedbackQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _RetellingFeedbackQuery);
const Retry = (_RetellingFeedbackRetryMutation.hash && _RetellingFeedbackRetryMutation.hash !== "307f51b712e60fa1d6f4d8ea500e1b4c" && console.error("The definition of 'RetellingFeedbackRetryMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _RetellingFeedbackRetryMutation);
type Page = {
  readonly number: number;
  readonly listenPassage: unknown;
};

/**
 * One retelling's feedback (listen & write design §5): the learner's text
 * with the shown suggestions underlined, each as "their words → the target"
 * with its explanation, the smoother version beside it, and one strength.
 * Called suggestions, never mistakes, and nothing counted (§10.9's clean-text
 * bar leaves room for a style nit on a correct text).
 *
 * While PENDING it polls `node(id:)` (§10.8): every 2 s, a busy note after
 * 30 s, and up to 11 minutes. The clock starts when this screen opens or a
 * retry is asked, not at `createdAt`: a retelling opened from the history
 * long after is either finished or the server has failed it by then.
 */
export function RetellingFeedback({
  id,
  bookId,
  pages
}: {
  id: string;
  bookId: string;
  pages: readonly Page[];
}) {
  const environment = useRelayEnvironment();
  const data = useLazyLoadQuery<RetellingFeedbackQueryType>(RetellingFeedbackQuery, {
    id
  });
  // `status` rather than `id`: Relay selects `id` on any node, and only a
  // Retelling has a status.
  const r = data.node?.status ? data.node : null;
  const [since, setSince] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [retryError, setRetryError] = useState<Message | null>(null);
  const [retry, retrying] = useMutation<RetellingFeedbackRetryMutation>(Retry);
  const {
    practise,
    busy,
    why
  } = usePractise();
  const status = (r?.status ?? 'PENDING') as Status;
  const wait = waiting(status, now - since);

  // Bumped by a retry answered CONFLICT: another tab already retried, so the
  // row is read again, by an effect whose cleanup ends the read on unmount.
  const [reread, setReread] = useState(0);
  useEffect(() => {
    if (reread === 0) return;
    const sub = fetchQuery<RetellingFeedbackQueryType>(environment, RetellingFeedbackQuery, {
      id
    }, {
      fetchPolicy: 'network-only'
    }).subscribe({});
    return () => sub.unsubscribe();
  }, [reread, environment, id]);
  const present = r !== null;
  useEffect(() => {
    if (!present || !wait.poll) return;
    let sub: {
      unsubscribe(): void;
    } | null = null;
    const timer = setTimeout(() => {
      // Network-only: the store holds the PENDING answer this replaces. The
      // response lands in the store, so the query above re-renders with it.
      sub = fetchQuery<RetellingFeedbackQueryType>(environment, RetellingFeedbackQuery, {
        id
      }, {
        fetchPolicy: 'network-only'
      }).subscribe({
        complete: () => setNow(Date.now()),
        error: () => setNow(Date.now())
      });
    }, POLL_MS);
    return () => {
      clearTimeout(timer);
      sub?.unsubscribe();
    };
  }, [present, wait.poll, now, environment, id]);
  if (!r || !r.status) return <p className="text-base-content/50 py-12 text-center">{t('listen-gone')}</p>;
  if (status === 'PENDING') {
    return <div className="flex flex-col items-center gap-4 py-8 text-center" aria-live="polite">
        {!wait.late && <Loader2 aria-hidden className="text-primary size-8 animate-spin" />}
        <p>{wait.late ? t('listen-late') : t('listen-waiting')}</p>
        {wait.busy && <p className="text-base-content/70 text-sm">{t('listen-busy')}</p>}
      </div>;
  }
  if (status === 'FAILED') {
    return <div className="flex flex-col items-center gap-4 py-8 text-center">
        <p>{t('listen-failed')}</p>
        <button type="button" className="btn btn-primary min-h-(--hit-min)" disabled={retrying} onClick={() => {
        setRetryError(null);
        retry({
          variables: {
            id: r.id ?? id
          },
          onCompleted: () => {
            setSince(Date.now());
            setNow(Date.now());
          },
          onError: err => {
            const code = codeFromError(err);
            // CONFLICT: it is no longer failed (another tab retried it),
            // so read it again rather than say the retry failed.
            if (code === 'CONFLICT') {
              setSince(Date.now());
              setNow(Date.now());
              setReread(n => n + 1);
            } else setRetryError(retryCopy(code));
          }
        });
      }}>
          {t('listen-retry')}
        </button>
        {retryError && <p role="alert" className="text-error text-sm">
            {t(retryError)}
          </p>}
      </div>;
  }
  const corrections = r.corrections ?? [];
  const pieces = underline(r.text ?? '', corrections);
  const next = pages.find(p => p.number === (r.lastPage ?? 0) + 1 && p.listenPassage);
  const book = r.book?.id === bookId ? r.book : null;
  return <div className="flex flex-col gap-6">
      <div className="grid gap-4 md:grid-cols-2">
        <section className="flex flex-col gap-2">
          <h2 className="font-semibold">{t('listen-your-text')}</h2>
          <p className="leading-relaxed whitespace-pre-wrap">
            <En>
              {pieces.map((p, k) => p.mark === null ? <span key={k}>{p.text}</span> : <span key={k} className="decoration-warning underline decoration-wavy decoration-2 underline-offset-4">
                    {p.text}
                    <sup className="text-base-content/60 ms-0.5" aria-hidden>
                      {p.mark + 1}
                    </sup>
                  </span>)}
            </En>
          </p>
        </section>
        {r.better && <section className="flex flex-col gap-2">
            <h2 className="font-semibold">{t('listen-better')}</h2>
            <p className="leading-relaxed whitespace-pre-wrap">
              <En>{r.better}</En>
            </p>
          </section>}
      </div>

      <section className="flex flex-col gap-2">
        <h2 className="font-semibold">{t('listen-suggestions')}</h2>
        {corrections.length === 0 ? <p className="text-base-content/70">{t('listen-no-suggestions')}</p> : <ol className="flex flex-col gap-2">
            {corrections.map((c, i) => <li key={c.id} className="border-base-300 rounded-box flex flex-col gap-1 border p-3">
                <p>
                  <span className="text-base-content/60 me-2 text-sm" aria-hidden>
                    {i + 1}
                  </span>
                  <En>
                    {c.learnerForm} → <strong>{c.targetForm}</strong>
                  </En>
                </p>
                <p className="text-base-content/70 text-sm">
                  <En>{c.explanation}</En>
                </p>
                <HearIt target={{
            errorRecordId: c.id
          }} />
              </li>)}
          </ol>}
      </section>

      {r.strength && <section className="flex flex-col gap-2">
          <h2 className="font-semibold">{t('listen-strength')}</h2>
          <p>
            <En>{r.strength}</En>
          </p>
        </section>}

      {book && <div className="flex flex-col gap-3">
          <button type="button" className="btn btn-secondary min-h-(--hit-min)" disabled={busy} onClick={() => practise({
        storyBookId: book.id
      })}>
            {t('listen-talk')}
          </button>
          {next && <Link to={`/books/${book.id}/listen/${next.number}`} className="btn btn-outline min-h-(--hit-min)">
              {t('listen-next-passage')}
            </Link>}
          {why && <p role="alert" className="text-error text-sm">
              {why}
            </p>}
        </div>}
    </div>;
}